import { Flex, Header, Tabs } from '@driftt/dds'
import { Subnav } from '@driftt/tide-subnav'
import { showTransientNotification } from '@driftt/traffic-control'
import * as R from 'ramda'
import React, { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'

import PlaybooksTableWrapper from 'components/PlaybooksTable/PlaybooksTableWrapper'
import { capitalizeFirstLetter, replaceWith } from 'utils'
import {
  userHasAnyPlaybookAccess,
  userHasChatPlaybooks,
  userHasEmailPlaybooks,
  userHasFastlanePlaybooks,
  userHasMeetingsPlaybooks,
} from 'utils/access'
import { getSubNavNodes } from 'utils/getSubNavNodes'

import PlaybooksCreationMenu from './PlaybooksCreationMenu'

const PATH_NAMES = {
  AUTOMATION: 'automation',
  CHAT: 'chat',
  EMAIL: 'email',
  FASTLANE: 'fastlane',
  SEQUENCE: 'sequence',
  MEETINGS: 'meeting',
}

const BASE_URL = '/list'

const getFilteredPathNames = (playbookAccess) => {
  const {
    userHasChatPlaybooks,
    userHasFastlanePlaybooks,
    userHasEmailPlaybooks,
    userHasMeetingsPlaybooks,
  } = playbookAccess

  return Object.keys(PATH_NAMES).filter((type) => {
    switch (type) {
      case 'CHAT':
        return userHasChatPlaybooks()
      case 'FASTLANE':
        return userHasFastlanePlaybooks()
      case 'EMAIL':
        return userHasEmailPlaybooks()
      case 'MEETINGS':
        return userHasMeetingsPlaybooks()
      default:
        return false
    }
  })
}

const PlaybooksTabsPage = ({
  orgUsers,
  showTransientNotification,
  history,
  match,
}) => {
  const SUB_NAV_NODES = getSubNavNodes()
  const hasAnyPlaybookAccess = userHasAnyPlaybookAccess()

  const [sidebarIsOpen, setSidebarIsOpen] = useState(true)
  const playbookType = useMemo(() => {
    return replaceWith(R.pathOr('', ['params', 'type'], match), '-', '_')
  }, [match])

  const [activeTab, setActiveTab] = useState(playbookType)

  const filteredPathNames = useMemo(
    () =>
      getFilteredPathNames({
        userHasChatPlaybooks,
        userHasFastlanePlaybooks,
        userHasEmailPlaybooks,
        userHasMeetingsPlaybooks,
      }),
    []
  )

  const playbookOptions = useMemo(
    () =>
      filteredPathNames.map((type) => ({
        label: capitalizeFirstLetter(type.toLocaleLowerCase()),
        value: type.toLocaleLowerCase(),
        content: (
          <PlaybooksTableWrapper
            playbookType={type}
            orgUsers={orgUsers}
            showTransientNotification={showTransientNotification}
            history={history}
          />
        ),
      })),
    [filteredPathNames, orgUsers, showTransientNotification, history]
  )

  useEffect(() => {
    const currentPath = history.location.pathname
    const expectedPath = `${BASE_URL}/${activeTab}`

    if (currentPath !== expectedPath) {
      history.push(expectedPath)
    }
  }, [activeTab, history])

  return (
    <>
      {hasAnyPlaybookAccess && (
        <Subnav
          title="Chatbot"
          nodes={SUB_NAV_NODES}
          open={sidebarIsOpen}
          onToggle={() => setSidebarIsOpen(!sidebarIsOpen)}
        />
      )}
      <Flex
        direction="column"
        gap={0}
        fullWidth
        css={{ backgroundColor: '$gray2', overflow: 'scroll' }}
      >
        <Flex
          direction="row"
          fullWidth
          justifyContent="space-between"
          css={{ backgroundColor: '$white', padding: '$3' }}
        >
          <Header variant="h2">Playbooks</Header>
          <PlaybooksCreationMenu
            hasAnyPlaybookAccess={hasAnyPlaybookAccess}
            pathNames={filteredPathNames}
            history={history}
          />
        </Flex>
        <Flex fullWidth style={{ paddingBottom: '180px' }}>
          <Tabs
            options={playbookOptions}
            defaultValue={playbookType}
            onValueChange={setActiveTab}
            tabListProps={{
              css: {
                paddingLeft: '$4',
                paddingRight: '$4',
                marginBottom: '0',
                background: '$white',
              },
            }}
          />
        </Flex>
      </Flex>
    </>
  )
}

const mapStateToProps = ({ org }) => ({
  orgUsers: org.users,
})

const mapDispatchToProps = (dispatch) => ({
  showTransientNotification: (config) => {
    dispatch(showTransientNotification(config))
  },
})

export default connect(mapStateToProps, mapDispatchToProps)(PlaybooksTabsPage)
